import React from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Checkbox } from "@/components/ui/checkbox";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { cn } from "@/lib/utils";
import {
  X,
  Plus,
  Minus,
  ChevronUp,
  ChevronDown,
  Users,
  Settings,
  Clock,
  Mail,
  User,
  MessageSquare,
  CheckCircle,
  AlertCircle,
  Video,
  UserCheck,
  Save,
  ArrowLeft,
  ArrowRight
} from "lucide-react";
import { toast } from "react-hot-toast";
import { useCreateOneOnOneSetup, useUpdateOneOnOneSetup } from "@/hooks/api-hooks";

interface TeamMember {
  id: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    fullName?: string;
    email: string;
  };
  role: 'owner' | 'admin' | 'manager' | 'staff';
}

type ExistingSetup = {
  id: string;
  title?: string | null;
  managerId: string;
  participantId: string;
  employeeId?: string;
  workflowType?: 'email' | 'physical';
  schedulePattern?: string | null;
  frequency?: string;
  duration?: number;
  defaultAgenda?: string[];
  questions?: Array<{
    id?: string;
    text?: string;
    type?: string;
    questionType?: string;
    options?: string[];
    isRequired?: boolean;
    order?: number;
  }>;
  isActive?: boolean;
  startDate?: string | null;
  startTime?: string | null;
  customCron?: string | null;
  nextScheduledDate?: string | null;
  managerProfile?: {
    id: string;
    firstName?: string;
    lastName?: string;
    fullName?: string;
    email?: string | null;
  } | null;
  participantProfile?: {
    id: string;
    firstName?: string;
    lastName?: string;
    fullName?: string;
    email?: string | null;
  } | null;
  completionDueDays?: number | null;
};

interface CreateOneOnOneSetupModalProps {
  isOpen: boolean;
  onClose: () => void;
  teamId: string;
  teamMembers: TeamMember[];
  onSuccess?: () => void;
  mode?: 'create' | 'edit';
  initialSetup?: ExistingSetup | null;
}

const questionSchema = z.object({
  id: z.string().optional(),
  text: z.string().min(3, "Question must be at least 3 characters"),
  type: z.enum(["text", "rating", "multiple_choice", "yes_no", "likert"]).optional(),
  questionType: z.enum(["text", "rating", "multiple_choice", "yes_no", "likert"]).optional(),
  options: z.array(z.string()).optional(),
  isRequired: z.boolean().default(true),
  order: z.number().optional(),
  sortOrder: z.number().optional(),
}).transform((data) => ({
  id: data.id || crypto.randomUUID(),
  text: data.text,
  type: data.type || data.questionType || 'text',
  questionType: data.questionType || data.type || 'text',
  options: data.options || [],
  isRequired: data.isRequired !== false,
  order: data.order ?? data.sortOrder ?? 0,
}));

const formSchema = z.object({
  title: z
    .string()
    .min(3, 'Please enter a setup name')
    .max(120, 'Setup name must be 120 characters or fewer'),
  managerId: z.string().min(1, "Please select a manager"),
  employeeId: z.string().min(1, "Please select an employee"),
  workflowType: z.enum(["email", "physical"]).default("email"),
  schedulePattern: z.enum(["bi-weekly", "weekly", "monthly", "manual", "custom"]).optional(),
  customCron: z.string().optional(),
  startDate: z.string().optional(),
  startTime: z.string().optional(),
  completionDueDays: z.number().min(1).max(30).optional(),
  questions: z.array(questionSchema).min(1, "Please add at least one question"),
  templateId: z.string().optional(),
  saveAsTemplate: z.boolean().default(false),
  templateName: z.string().optional(),
  templateDescription: z.string().optional(),
}).refine((data) => {
  if (data.workflowType === "email") {
    if (!data.schedulePattern) return false;
    if (!data.startDate || !data.startTime) return false;
    if (data.schedulePattern === "custom" && (!data.customCron || data.customCron.trim().length === 0)) return false;
  }
  return true;
}, {
  message: "Schedule pattern, start date and time are required for email 1-on-1s",
  path: ["schedulePattern"]
}).refine((data) => {
  if (data.workflowType === "email" && data.schedulePattern === "custom") {
    return !!(data.customCron && data.customCron.trim().length > 0);
  }
  return true;
}, {
  message: "Please provide a cron expression for custom schedule",
  path: ["customCron"]
}).refine((data) => {
  if (data.saveAsTemplate) {
    return !!(data.templateName && data.templateName.trim().length > 0);
  }
  return true;
}, {
  message: "Please provide a template name",
  path: ["templateName"]
}).refine((data) => {
  if (data.managerId && data.employeeId) {
    return data.managerId !== data.employeeId;
  }
  return true;
}, {
  message: "Manager and employee must be different people",
  path: ["employeeId"]
}).refine((data) => {
  if (data.workflowType === 'email') {
    return typeof data.completionDueDays === 'number' && !Number.isNaN(data.completionDueDays);
  }
  return true;
}, {
  message: 'Please set a due window (1-30 days) for email 1-on-1s',
  path: ['completionDueDays']
});

export type Setup1on1FormValues = z.infer<typeof formSchema>;
type QuestionType = Setup1on1FormValues['questions'][number]['type'];

const SAMPLE_QUESTIONS: Array<{
  id: string;
  text: string;
  type: QuestionType;
  questionType: QuestionType;
  options: string[];
  isRequired: boolean;
  order: number;
}> = [
  {
    id: "q1",
    text: "How are you feeling about your current workload?",
    type: "rating",
    questionType: "rating",
    options: [],
    isRequired: true,
    order: 1,
  },
  {
    id: "q2",
    text: "What's one thing I could do better as your manager?",
    type: "text",
    questionType: "text",
    options: [],
    isRequired: false,
    order: 2,
  },
  {
    id: "q3",
    text: "Do you feel challenged in your current role?",
    type: "multiple_choice",
    questionType: "multiple_choice",
    options: ["Very challenged", "Somewhat challenged", "Not challenged enough", "Overwhelmed"],
    isRequired: true,
    order: 3,
  },
  {
    id: "q4",
    text: "How satisfied are you with your career development opportunities?",
    type: "rating",
    questionType: "rating",
    options: [],
    isRequired: false,
    order: 4,
  },
];

type SampleQuestion = (typeof SAMPLE_QUESTIONS)[number];
type TemplateQuestionInput = SampleQuestion | {
  id?: string;
  text?: string;
  type?: string;
  questionType?: string;
  options?: string[];
  isRequired?: boolean;
  order?: number;
};

const QUICK_TEMPLATES: Array<{
  id: string;
  name: string;
  description: string;
  questions: TemplateQuestionInput[];
}> = [
  {
    id: 'standard-template',
    name: 'Standard 1-on-1 Template',
    description: 'one_on_one',
    questions: [SAMPLE_QUESTIONS[0], SAMPLE_QUESTIONS[1], SAMPLE_QUESTIONS[2]],
  },
  {
    id: 'weekly-template',
    name: 'Weekly One-on-One Template',
    description: 'one_on_one',
    questions: [
      SAMPLE_QUESTIONS[0],
      SAMPLE_QUESTIONS[3],
      {
        id: 'q5',
        text: 'What is one goal you want to achieve before our next meeting?',
        type: 'text',
        questionType: 'text',
        options: [],
        isRequired: true,
        order: 5,
      },
    ],
  },
];

const TAB_ORDER = ['setup', 'questions', 'review'] as const;

const normalizeFrequency = (
  input?: string | null,
): 'manual' | 'custom' | 'bi-weekly' | 'monthly' | 'weekly' => {
  if (!input) return 'manual';
  const value = input.toLowerCase();
  if (value === 'custom') return 'custom';
  if (value === 'weekly') return 'weekly';
  if (value === 'biweekly' || value === 'bi-weekly') return 'bi-weekly';
  if (value === 'monthly') return 'monthly';
  return 'manual';
};

function QuestionItem({ control, index, onRemove, onMoveUp, onMoveDown, canMoveUp, canMoveDown, watch, setValue }: any) {
  const questionType = watch(`questions.${index}.type`);
  const questionOptions = watch(`questions.${index}.options`) || [];
  return (
    <div className={`relative flex items-start gap-4 p-6 bg-background border rounded-2xl mb-4 transition-smooth border-border hover:border-primary/30`}>
      <div className="flex flex-col gap-2 self-center">
        <Button type="button" variant="ghost" size="icon" className="h-8 w-8 rounded-xl" onClick={() => onMoveUp(index)} disabled={!canMoveUp}>
          <ChevronUp className="h-4 w-4" />
        </Button>
        <Button type="button" variant="ghost" size="icon" className="h-8 w-8 rounded-xl" onClick={() => onMoveDown(index)} disabled={!canMoveDown}>
          <ChevronDown className="h-4 w-4" />
        </Button>
      </div>

      <div className="flex-1 space-y-4">
        <FormField control={control} name={`questions.${index}.text`} render={({ field }) => (
          <FormItem>
            <FormControl>
              <Input {...field} placeholder="Enter question text" className="h-12 rounded-xl border-2 border-border focus:border-primary focus:ring-0" />
            </FormControl>
          </FormItem>
        )} />

        <div className="flex gap-4 items-center">
          <FormField control={control} name={`questions.${index}.type`} render={({ field }) => (
            <FormItem>
              <FormControl>
                <select {...field} className="px-4 py-3 rounded-xl border-2 border-border bg-background text-sm font-medium focus:border-primary focus:ring-0 !bg-background text-foreground">
                  <option value="text">Text Response</option>
                  <option value="rating">Rating (1-10)</option>
                  <option value="multiple_choice">Multiple Choice</option>
                  <option value="yes_no">Yes/No</option>
                  <option value="likert">Likert Scale</option>
                </select>
              </FormControl>
            </FormItem>
          )} />

          <FormField control={control} name={`questions.${index}.isRequired`} render={({ field }) => (
            <FormItem>
              <FormControl>
                <label className="flex items-center gap-3 bg-muted px-4 py-3 rounded-xl">
                  <Checkbox checked={field.value} onCheckedChange={field.onChange} className="h-4 w-4" />
                  <span className="text-sm font-medium text-brand">Required</span>
                </label>
              </FormControl>
            </FormItem>
          )} />
        </div>

        {questionType === "multiple_choice" && (
          <div className="space-y-3 p-4 bg-gradient-to-br from-primary/5 to-primary/10 rounded-xl border border-primary/20 dark:from-surface-variant dark:to-surface-muted dark:border-border">
            <p className="text-sm font-medium text-primary dark:text-primary mb-3">Multiple Choice Options</p>
            {questionOptions.map((option: any, optionIndex: number) => (
              <div key={optionIndex} className="flex gap-3">
                <Input value={option} onChange={(e) => {
                  const newOptions = [...questionOptions];
                  newOptions[optionIndex] = e.target.value;
                  setValue(`questions.${index}.options`, newOptions);
                }} placeholder={`Option ${optionIndex + 1}`} className="flex-1 h-10 rounded-lg border-2 border-border focus:border-primary focus:ring-0" />
                <Button type="button" variant="ghost" size="icon" className="h-10 w-10 rounded-lg border-2 border-destructive/15 text-destructive hover:bg-destructive/5" onClick={() => {
                  const newOptions = [...questionOptions];
                  newOptions.splice(optionIndex, 1);
                  setValue(`questions.${index}.options`, newOptions);
                }}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={() => {
              const newOptions = [...questionOptions, ""];
              setValue(`questions.${index}.options`, newOptions);
            }} className="w-full h-10 rounded-lg border-2 border-primary/30 text-primary hover:bg-primary/10">
              <Plus className="h-4 w-4 mr-2" /> Add Option
            </Button>
          </div>
        )}
      </div>

      <Button type="button" variant="ghost" size="icon" className="h-10 w-10 rounded-xl border-2 border-destructive/15 text-destructive hover:bg-destructive/5" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRemove(index); }} title="Remove question">
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}

export function CreateOneOnOneSetupModal({
  isOpen,
  onClose,
  teamId,
  teamMembers,
  onSuccess,
  mode = 'create',
  initialSetup = null,
}: CreateOneOnOneSetupModalProps) {
  const createSetupMutation = useCreateOneOnOneSetup();
  const updateSetupMutation = useUpdateOneOnOneSetup();
  const [activeTab, setActiveTab] = React.useState("setup");

  const normalizedMembers = React.useMemo(() => {
    const cloned = teamMembers.map((member) => ({ ...member }));
    const seen = new Set(cloned.map((member) => member.user.id));

    if (mode === 'edit' && initialSetup) {
      const ensureMember = (profile: ExistingSetup['managerProfile'], role: TeamMember['role']) => {
        if (!profile || seen.has(profile.id)) return;
        seen.add(profile.id);
        cloned.push({
          id: profile.id,
          role,
          user: {
            id: profile.id,
            firstName: profile.firstName ?? profile.fullName ?? 'Member',
            lastName: profile.lastName ?? '',
            fullName: profile.fullName ?? `${profile.firstName ?? ''} ${profile.lastName ?? ''}`.trim(),
            email: profile.email ?? '',
          },
        });
      };

      ensureMember(initialSetup.managerProfile, 'manager');
      ensureMember(initialSetup.participantProfile, 'staff');
    }

    return cloned;
  }, [teamMembers, initialSetup, mode]);

  const managers = normalizedMembers.filter((member) => {
    const role = typeof member.role === 'string' ? member.role.toLowerCase() : '';
    return ['owner', 'admin', 'manager'].includes(role);
  });

  const staff = normalizedMembers.filter((member) => {
    const role = typeof member.role === 'string' ? member.role.toLowerCase() : '';
    return role === 'staff' || role === 'member' || role === 'team_lead';
  });

  const formatDateForInput = React.useCallback((value?: string | null) => {
    if (!value) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return '';
    }
    return parsed.toISOString().split('T')[0];
  }, []);

  const formatTimeForInput = React.useCallback((value?: string | null) => {
    if (!value) return '';
    if (/^\d{2}:\d{2}$/.test(value)) return value;
    const parsed = new Date(`1970-01-01T${value}`);
    if (Number.isNaN(parsed.getTime())) {
      const match = value.match(/(\d{2}:\d{2})/);
      return match ? match[1] : '';
    }
    return parsed.toISOString().split('T')[1]?.slice(0, 5) ?? '';
  }, []);

  const getDefaultValues = React.useCallback((): Setup1on1FormValues => {
    const getDefaultStartDate = () => {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      return tomorrow.toISOString().split('T')[0];
    };
    const getDefaultStartTime = () => '09:00';

    if (mode === 'edit' && initialSetup) {
      const startDate = formatDateForInput(initialSetup.startDate || initialSetup.nextScheduledDate) || getDefaultStartDate();
      const startTime = formatTimeForInput(initialSetup.startTime) || getDefaultStartTime();
      const schedulePattern = initialSetup.schedulePattern || (initialSetup.workflowType === 'email' ? 'bi-weekly' : 'manual');

      const questions = Array.isArray(initialSetup.questions)
        ? initialSetup.questions.map((question, index) => {
            const resolvedType = (question.type ?? question.questionType ?? 'text') as QuestionType;
            return {
              id: question.id || `existing-${index}`,
              text: question.text || '',
              type: resolvedType,
              questionType: (question.questionType ?? question.type ?? 'text') as QuestionType,
              options: Array.isArray(question.options) ? question.options : [],
              isRequired: question.isRequired !== false,
              order: typeof question.order === 'number' ? question.order : index,
            };
          })
        : [];

      const title = initialSetup.title && initialSetup.title.trim().length > 0 ? initialSetup.title.trim() : '1-on-1 Session';

      return {
        title,
        managerId: initialSetup.managerId || '',
        employeeId: initialSetup.participantId || initialSetup.employeeId || '',
        workflowType: initialSetup.workflowType || 'email',
        schedulePattern: schedulePattern as Setup1on1FormValues['schedulePattern'],
        customCron: initialSetup.customCron || '',
        startDate,
        startTime,
        completionDueDays: initialSetup.completionDueDays ?? (initialSetup.workflowType === 'email' ? 7 : undefined),
        questions,
        templateId: '',
        saveAsTemplate: false,
        templateName: '',
        templateDescription: '',
      };
    }

    return {
      title: '1-on-1 Session',
      managerId: '',
      employeeId: '',
      workflowType: 'email',
      schedulePattern: 'bi-weekly',
      customCron: '',
      startDate: getDefaultStartDate(),
      startTime: getDefaultStartTime(),
      completionDueDays: 7,
      questions: [],
      templateId: '',
      saveAsTemplate: false,
      templateName: '',
      templateDescription: '',
    };
  }, [initialSetup, mode, formatDateForInput, formatTimeForInput]);

  const form = useForm<Setup1on1FormValues>({ resolver: zodResolver(formSchema), defaultValues: getDefaultValues() });
  const { control, handleSubmit, watch, setValue, getValues, trigger, formState: { errors, dirtyFields } } = form;
  const { fields: questionFields, append: appendQuestion, remove: removeQuestionByIndex, move: moveQuestion, replace: replaceQuestions } = useFieldArray({ control, name: "questions" });
  const watchedWorkflowType = watch("workflowType");
  const watchedSchedulePattern = watch("schedulePattern");
  const watchedSaveAsTemplate = watch("saveAsTemplate");
  const watchedManagerId = watch("managerId");
  const watchedEmployeeId = watch("employeeId");
  const watchedTitle = watch("title");
  const watchedCompletionDueDays = watch("completionDueDays");

  const safeWatchedQuestions = React.useMemo(() => questionFields.map((field) => ({ ...field, _key: field.id })), [questionFields]);

  const getMemberDisplayName = React.useCallback((memberId?: string) => {
    if (!memberId) return '';
    const member = normalizedMembers.find((entry: any) => (entry?.user?.id ?? entry?.id) === memberId);
    if (!member) {
      if (mode === 'edit' && initialSetup) {
        if (initialSetup.managerProfile?.id === memberId) {
          return initialSetup.managerProfile.fullName ?? initialSetup.managerProfile.firstName ?? 'Manager';
        }
        if (initialSetup.participantProfile?.id === memberId) {
          return initialSetup.participantProfile.fullName ?? initialSetup.participantProfile.firstName ?? 'Team member';
        }
      }
      return 'Member';
    }
    const user = member.user ?? member;
    const firstName = (user as { firstName?: string }).firstName ?? (user as Record<string, string | undefined>).first_name ?? '';
    const lastName = (user as { lastName?: string }).lastName ?? (user as Record<string, string | undefined>).last_name ?? '';
    return (user.fullName || `${firstName} ${lastName}`.trim() || user.email || 'Member');
  }, [normalizedMembers, initialSetup, mode]);
  const questionCount = safeWatchedQuestions.length;
  const hasQuestions = questionCount > 0;
  const modalTitle = mode === 'edit' ? 'Edit 1-on-1 Setup' : 'Setup 1-on-1';
  const modalDescription = mode === 'edit'
    ? 'Update recurring 1-on-1 sessions configuration'
    : 'Create recurring 1-on-1 sessions configuration';

  React.useEffect(() => {
    if (mode === 'edit' && initialSetup && isOpen) {
      form.reset(getDefaultValues());
      setActiveTab('setup');
    }
    if (!isOpen) {
      setActiveTab('setup');
    }
  }, [form, getDefaultValues, initialSetup, isOpen, mode]);

  React.useEffect(() => {
    if (watchedWorkflowType && !watchedSchedulePattern) {
      const defaultPattern = watchedWorkflowType === 'email' ? 'bi-weekly' : 'manual';
      setValue('schedulePattern', defaultPattern);
    }
  }, [watchedWorkflowType, watchedSchedulePattern, setValue]);

  React.useEffect(() => {
    if (watchedWorkflowType === 'email') {
      const currentDue = getValues('completionDueDays');
      if (currentDue === undefined || currentDue === null || Number.isNaN(currentDue)) {
        setValue('completionDueDays', initialSetup?.completionDueDays ?? 7, { shouldValidate: true });
      }
    } else if (getValues('completionDueDays') !== undefined) {
      setValue('completionDueDays', undefined, { shouldValidate: true });
    }
  }, [watchedWorkflowType, getValues, setValue, initialSetup?.completionDueDays]);

  React.useEffect(() => {
    if (mode === 'edit') return;

    const currentTitle = getValues('title') ?? '';

    if (!watchedEmployeeId) {
      if (!dirtyFields.title || currentTitle.trim().length === 0 || currentTitle.endsWith('1-on-1 Session')) {
        if (currentTitle !== '1-on-1 Session') {
          setValue('title', '1-on-1 Session', { shouldDirty: false });
        }
      }
      return;
    }

    if (dirtyFields.title && currentTitle && currentTitle !== '1-on-1 Session') {
      return;
    }

    const employeeName = getMemberDisplayName(watchedEmployeeId) || 'Member';
    const autoTitle = `${employeeName} 1-on-1 Session`.trim();

    if (currentTitle !== autoTitle) {
      setValue('title', autoTitle, { shouldDirty: false });
    }
  }, [watchedEmployeeId, dirtyFields.title, getMemberDisplayName, getValues, mode, setValue]);

  const addQuestion = React.useCallback(() => {
    appendQuestion({ id: `q${Date.now()}`, text: "", type: "text", questionType: "text", options: [], isRequired: true, order: questionFields.length });
  }, [appendQuestion, questionFields.length]);

  const removeQuestion = React.useCallback((index: number) => {
    if (questionFields.length <= 1) {
      toast('You must have at least one question', { icon: '⚠️' });
      return;
    }
    removeQuestionByIndex(index);
  }, [removeQuestionByIndex, questionFields.length]);

  const moveQuestionUp = (index: number) => { if (index > 0) moveQuestion(index, index - 1); };
  const moveQuestionDown = (index: number) => { if (index < questionFields.length - 1) moveQuestion(index, index + 1); };

  const [isTogglingQuestion, setIsTogglingQuestion] = React.useState<string | null>(null);
  const [isAddingAllQuestions, setIsAddingAllQuestions] = React.useState(false);

  const toggleQuestionSelection = React.useCallback((question: typeof SAMPLE_QUESTIONS[number]) => {
    if (isAddingAllQuestions) return;
    if (isTogglingQuestion === question.id) return;
    setIsTogglingQuestion(question.id);

    const currentQuestions = getValues("questions") || [];
    const isCurrentlySelected = currentQuestions.some((q: any) => q.id === question.id);
    let result;
    if (isCurrentlySelected) {
      result = currentQuestions.filter((q: any) => q.id !== question.id);
    } else {
      const resolvedType = (question.questionType ?? question.type ?? 'text') as QuestionType;
      const newQuestion = {
        ...question,
        id: question.id || `q${Date.now()}-${Math.random()}`,
        type: resolvedType,
        questionType: resolvedType,
        options: question.options || [],
        isRequired: question.isRequired !== undefined ? question.isRequired : true,
        order: currentQuestions.length,
      };
      result = [...currentQuestions, newQuestion];
    }
    replaceQuestions(result);
    setValue("questions", result, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
    setTimeout(() => setIsTogglingQuestion(null), 150);
  }, [setValue, getValues, isTogglingQuestion, isAddingAllQuestions, replaceQuestions]);

  const toggleAllQuestions = React.useCallback(() => {
    setIsAddingAllQuestions(true);
    const currentQuestions = getValues("questions") || [];
    const currentQuestionIds = new Set(currentQuestions.map((q: any) => q.id));
    const allAdded = SAMPLE_QUESTIONS.every((q: any) => currentQuestionIds.has(q.id));
    let updatedQuestions;
    if (allAdded) {
      const sampleIds = new Set(SAMPLE_QUESTIONS.map((q) => q.id));
      updatedQuestions = currentQuestions.filter((q: any) => !sampleIds.has(q.id));
    } else {
      const newQuestions = SAMPLE_QUESTIONS
        .filter((q) => !currentQuestionIds.has(q.id))
        .map((q, index): SampleQuestion => {
          const resolvedType = (q.questionType ?? q.type ?? 'text') as QuestionType;
          return {
            ...q,
            id: q.id,
            type: resolvedType,
            questionType: resolvedType,
            options: Array.isArray(q.options) ? q.options : [],
            isRequired: q.isRequired !== undefined ? q.isRequired : true,
            order: currentQuestions.length + index,
          };
        });
      updatedQuestions = [...currentQuestions, ...newQuestions];
    }
    replaceQuestions(updatedQuestions);
    setValue("questions", updatedQuestions, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
    setIsAddingAllQuestions(false);
  }, [setValue, getValues, replaceQuestions]);

  const applyQuickTemplate = React.useCallback((templateId: string) => {
    const template = QUICK_TEMPLATES.find((item) => item.id === templateId);
    if (!template) return;

    const templateQuestions = template.questions.map((question, index) => {
      const resolvedType = ((question.type ?? question.questionType) ?? 'text') as QuestionType;
      const text = typeof question.text === 'string' ? question.text : `Question ${index + 1}`;
      return {
        id: question.id ?? `template-question-${index}`,
        text,
        type: resolvedType,
        questionType: resolvedType,
        options: Array.isArray(question.options) ? question.options : [],
        isRequired: question.isRequired !== false,
        order: index,
      } satisfies SampleQuestion;
    });

    replaceQuestions(templateQuestions);
    setValue('questions', templateQuestions, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
    toast.success(`${template.name} loaded`);
  }, [replaceQuestions, setValue]);

  const selectedQuestionIds = React.useMemo(() => new Set(safeWatchedQuestions.map(q => q.id)), [safeWatchedQuestions]);
  const areAllSampleQuestionsSelected = React.useMemo(
    () => SAMPLE_QUESTIONS.every((question) => selectedQuestionIds.has(question.id)),
    [selectedQuestionIds],
  );

  const getScheduleDescription = (schedulePattern: string | undefined) => {
    switch (schedulePattern) {
      case "bi-weekly": return "Every two weeks";
      case "monthly": return "Once per month";
      case "manual": return "Scheduled manually as needed";
      case "custom": return "Custom cron schedule";
      default: return "";
    }
  };

  const getManagerName = () => {
    const managerId = watchedManagerId;
    const name = getMemberDisplayName(managerId);
    return name && name.trim().length > 0 ? name : 'Not selected';
  };

  const getEmployeeName = () => {
    const employeeId = watch('employeeId');
    const name = getMemberDisplayName(employeeId);
    return name && name.trim().length > 0 ? name : 'Not selected';
  };

  const handleFormSubmit: (data: Setup1on1FormValues) => Promise<any> = async (data) => {
    const uniqueQuestions = Array.isArray(data.questions)
      ? data.questions
          .filter(q => q && q.text && q.text.trim().length >= 3)
          .reduce((acc, current) => {
            const exists = acc.find(q => q.text?.trim().toLowerCase() === current.text?.trim().toLowerCase());
            if (!exists) {
              const resolvedType = (current.type ?? current.questionType ?? 'text') as QuestionType;
              acc.push({
                id: current.id || crypto.randomUUID(),
                text: current.text.trim(),
                type: resolvedType,
                questionType: resolvedType,
                options: Array.isArray(current.options) ? current.options : [],
                isRequired: current.isRequired !== false,
                order: current.order || acc.length,
              });
            }
            return acc;
          }, [] as Array<Setup1on1FormValues['questions'][number]>)
      : [];

    const normalizedPattern = normalizeFrequency(
      data.schedulePattern ?? initialSetup?.schedulePattern ?? initialSetup?.frequency ?? 'manual',
    );

    const basePayload = {
      title: data.title?.trim() && data.title.trim().length > 0 ? data.title.trim() : '1-on-1 Session',
      managerId: data.managerId,
      participantId: data.employeeId,
      workflowType: data.workflowType,
      frequency: normalizedPattern,
      schedulePattern: normalizedPattern,
      customCron:
        data.workflowType === 'email' && data.schedulePattern === 'custom'
          ? data.customCron || initialSetup?.customCron || undefined
          : undefined,
      startDate:
        data.workflowType === 'email' && data.startDate && data.startDate.trim().length > 0
          ? data.startDate
          : undefined,
      startTime:
        data.workflowType === 'email' && data.startTime && data.startTime.trim().length > 0
          ? data.startTime
          : undefined,
      emailDueDays:
        data.workflowType === 'email'
          ? (typeof data.completionDueDays === 'number' ? data.completionDueDays : initialSetup?.completionDueDays ?? 7)
          : undefined,
      questions: uniqueQuestions,
      defaultAgenda: initialSetup?.defaultAgenda || [],
      duration: initialSetup?.duration || 30,
      isActive: mode === 'edit' ? (initialSetup?.isActive ?? true) : true,
    };

    try {
      if (mode === 'edit' && initialSetup?.id) {
        await updateSetupMutation.mutateAsync({
          setupId: initialSetup.id,
          teamId,
          ...basePayload,
        }, {
          onError: (error: any) => {
            throw error;
          },
        });
        toast.dismiss();
        toast.success('1-on-1 setup updated');
      } else {
        await createSetupMutation.mutateAsync({
          teamId,
          ...basePayload,
          saveAsTemplate: data.saveAsTemplate,
          templateName: data.saveAsTemplate ? data.templateName : undefined,
          templateDescription: data.saveAsTemplate ? data.templateDescription : undefined,
          action: 'create',
        }, {
          onError: (error: any) => {
            throw error;
          },
        });
        toast.dismiss();
        toast.success('1-on-1 setup created successfully');
      }

      onSuccess?.();
      form.reset(getDefaultValues());
      setActiveTab('setup');
      onClose();
    } catch (error: any) {
      console.error('1-on-1 setup submission failed:', error);
      toast.dismiss();
      const message = error?.response?.data?.error || error?.message || 'Failed to save 1-on-1 setup';
      toast.error(message);
      throw error;
    }
  };

  const currentTabIndex = TAB_ORDER.indexOf(activeTab as typeof TAB_ORDER[number]);
  const isFirstTab = currentTabIndex <= 0;
  const isLastTab = currentTabIndex === TAB_ORDER.length - 1;
  const isSubmitting = mode === 'edit' ? updateSetupMutation.isPending : createSetupMutation.isPending;

  const goToPreviousTab = () => {
    if (isFirstTab) return;
    setActiveTab(TAB_ORDER[currentTabIndex - 1]);
  };

  const goToNextTab = async () => {
    if (isLastTab) return;

    if (activeTab === 'setup') {
      const fieldsToValidate = ['title', 'managerId', 'employeeId', 'workflowType'];
      if (watchedWorkflowType === 'email') {
        fieldsToValidate.push('schedulePattern', 'startDate', 'startTime', 'completionDueDays');
      }
      const isValid = await trigger(fieldsToValidate as any);
      if (!isValid) {
        toast.error('Please complete the required setup details');
        return;
      }
    }

    if (activeTab === 'questions' && safeWatchedQuestions.length === 0) {
      toast.error('Add at least one question before continuing');
      return;
    }

    setActiveTab(TAB_ORDER[currentTabIndex + 1]);
  };

  const handleCancel = () => {
    form.reset(getDefaultValues());
    setActiveTab('setup');
    setIsAddingAllQuestions(false);
    setIsTogglingQuestion(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleCancel(); }}>
      <DialogContent className="max-w-7xl w-[95vw] max-h-[95vh] overflow-hidden flex flex-col bg-background !bg-background border border-border">
        <DialogHeader className="pb-6 border-b border-border bg-background">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-primary/10 to-primary/20 rounded-2xl flex items-center justify-center">
              <UserCheck className="h-6 w-6 text-primary" />
            </div>
            <div>
              <DialogTitle className="text-2xl font-bold text-brand">{modalTitle}</DialogTitle>
              <DialogDescription className="text-brand-secondary">{modalDescription}</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto bg-background">
          <Form {...form}>
            <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6 p-6 bg-background">
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid w-full grid-cols-3 mb-8 h-14 rounded-2xl p-2 bg-muted">
                  <TabsTrigger value="setup" className="flex items-center gap-2 rounded-xl font-semibold"><Settings className="h-4 w-4" />Setup</TabsTrigger>
                  <TabsTrigger value="questions" className="flex items-center gap-2 rounded-xl font-semibold"><MessageSquare className="h-4 w-4" />Questions ({questionCount})</TabsTrigger>
                  <TabsTrigger value="review" className="flex items-center gap-2 rounded-xl font-semibold"><CheckCircle className="h-4 w-4" />Review</TabsTrigger>
                </TabsList>

                <TabsContent value="setup" className="space-y-8">
                  <FormField control={control} name="title" render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-semibold text-brand">Setup Name</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="e.g., Bi-weekly Product Sync"
                          className="h-12 rounded-xl border-2 border-border focus:border-primary focus:ring-0"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField control={control} name="managerId" render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-semibold text-brand">Manager</FormLabel>
                        <FormControl>
                          <Select value={field.value} onValueChange={field.onChange}>
                            <SelectTrigger className="h-12 bg-background border-border">
                              <div className="flex items-center gap-2 truncate">
                                <User className="h-4 w-4 text-muted-foreground" />
                                <span className="truncate text-left">
                                  {field.value ? getMemberDisplayName(field.value) : 'Select a manager'}
                                </span>
                              </div>
                            </SelectTrigger>
                            <SelectContent>
                              {managers?.map((member: any) => {
                                const user = member?.user || member;
                                return (
                                  <SelectItem key={user.id} value={user.id}>
                                    <div className="flex items-center gap-2">
                                      <User className="h-4 w-4" />
                                      {getMemberDisplayName(user.id)}
                                    </div>
                                  </SelectItem>
                                );
                              })}
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />

                    <FormField control={control} name="employeeId" render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-semibold text-brand">Employee</FormLabel>
                        <FormControl>
                          <Select value={field.value} onValueChange={field.onChange}>
                            <SelectTrigger className="h-12 bg-background border-border">
                              <div className="flex items-center gap-2 truncate">
                                <User className="h-4 w-4 text-muted-foreground" />
                                <span className="truncate text-left">
                                  {field.value ? getMemberDisplayName(field.value) : 'Select an employee'}
                                </span>
                              </div>
                            </SelectTrigger>
                            <SelectContent>
                              {staff?.map((member: any) => {
                                const user = member?.user || member;
                                if (user.id === watch("managerId")) return null;
                                return (
                                  <SelectItem key={user.id} value={user.id}>
                                    <div className="flex items-center gap-2">
                                      <User className="h-4 w-4" />
                                      {getMemberDisplayName(user.id)}
                                    </div>
                                  </SelectItem>
                                );
                              })}
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                  </div>

                  <FormField control={control} name="workflowType" render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-semibold text-brand">1-on-1 Type</FormLabel>
                      <FormControl>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                          <label className={`cursor-pointer p-6 rounded-2xl border-2 transition-all ${field.value === "email" ? "border-primary bg-primary/10" : "border-border bg-background hover:border-primary/30"}`}>
                            <input type="radio" value="email" checked={field.value === "email"} onChange={() => field.onChange("email")} className="sr-only" />
                            <div className="flex items-start gap-4">
                              <div className={`${field.value === "email" ? "bg-primary/20" : "bg-muted"} w-12 h-12 rounded-2xl flex items-center justify-center`}>
                                <Mail className="h-6 w-6 text-primary" />
                              </div>
                              <div>
                                <h3 className="font-semibold text-brand mb-2">Email 1-on-1</h3>
                                <p className="text-sm text-brand-secondary mb-3">Creates a reusable template with automated scheduling. Employee receives forms via email on schedule.</p>
                                <div className="flex items-center gap-2">
                                  <Badge variant="outline" className="text-xs">Recurring Template</Badge>
                                  <Badge variant="outline" className="text-xs">Automated</Badge>
                                </div>
                              </div>
                            </div>
                          </label>

                          <label className={`cursor-pointer p-6 rounded-2xl border-2 transition-all ${field.value === "physical" ? "border-primary bg-primary/10" : "border-border bg-background hover:border-primary/30"}`}>
                            <input type="radio" value="physical" checked={field.value === "physical"} onChange={() => field.onChange("physical")} className="sr-only" />
                            <div className="flex items-start gap-4">
                              <div className={`${field.value === "physical" ? "bg-primary/20" : "bg-muted"} w-12 h-12 rounded-2xl flex items-center justify-center`}>
                                <Video className="h-6 w-6 text-primary" />
                              </div>
                              <div>
                                <h3 className="font-semibold text-brand mb-2">Physical 1-on-1</h3>
                                <p className="text-sm text-brand-secondary mb-3">Creates an immediate working form for your meeting. Add context, rate temperature/productivity/support in real-time.</p>
                                <div className="flex items-center gap-2">
                                  <Badge variant="outline" className="text-xs">Immediate Form</Badge>
                                  <Badge variant="outline" className="text-xs">Live Meeting</Badge>
                                </div>
                              </div>
                            </div>
                          </label>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />

                  {watchedWorkflowType === "email" && (
                    <div className="space-y-6">
                      <div className="border-t border-border pt-6">
                        <h3 className="text-lg font-semibold text-brand mb-4">Schedule Settings</h3>
                        <p className="text-sm text-brand-secondary mb-6">Configure when and how often to send 1-on-1 forms to the employee</p>
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <FormField control={control} name="schedulePattern" render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-semibold text-brand">Frequency</FormLabel>
                            <Select onValueChange={field.onChange} value={field.value}>
                              <FormControl>
                                <SelectTrigger className="h-12 rounded-xl border-2 border-border focus:border-primary bg-background text-foreground">
                                  <SelectValue placeholder="Select frequency" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent className="bg-background border border-border shadow-clean">
                                <SelectItem value="bi-weekly" className="bg-background hover:bg-muted text-foreground">Bi-weekly</SelectItem>
                                <SelectItem value="monthly" className="bg-background hover:bg-muted text-foreground">Monthly</SelectItem>
                                <SelectItem value="manual" className="bg-background hover:bg-muted text-foreground">Manual</SelectItem>
                                <SelectItem value="custom" className="bg-background hover:bg-muted text-foreground">Custom</SelectItem>
                              </SelectContent>
                            </Select>
                            <p className="text-xs text-brand-secondary"><Clock className="h-3 w-3 inline mr-1" />{getScheduleDescription(watchedSchedulePattern)}</p>
                            <FormMessage />
                          </FormItem>
                        )} />

                        {watchedSchedulePattern === "custom" && (
                          <FormField control={control} name="customCron" render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-semibold text-brand">Custom Cron Expression</FormLabel>
                              <FormControl>
                                <Input {...field} placeholder="0 9 * * 1" className="h-12 rounded-xl border-2 border-border focus:border-primary focus:ring-0" />
                              </FormControl>
                              <p className="text-xs text-brand-secondary">Format: minute hour day month weekday</p>
                              <FormMessage />
                            </FormItem>
                          )} />
                        )}
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <FormField control={control} name="startDate" render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-semibold text-brand">Start Date</FormLabel>
                            <FormControl>
                              <Input {...field} type="date" className="h-12 rounded-xl border-2 border-border focus:border-primary focus:ring-0" min={new Date().toISOString().split('T')[0]} />
                            </FormControl>
                            <p className="text-xs text-brand-secondary">When to send the first 1-on-1 form</p>
                            <FormMessage />
                          </FormItem>
                        )} />

                        <FormField control={control} name="startTime" render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-semibold text-brand">Start Time</FormLabel>
                            <FormControl>
                              <Input {...field} type="time" className="h-12 rounded-xl border-2 border-border focus:border-primary focus:ring-0" />
                            </FormControl>
                            <p className="text-xs text-brand-secondary">Time of day to send forms</p>
                            <FormMessage />
                          </FormItem>
                        )} />

                        <FormField control={control} name="completionDueDays" render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-semibold text-brand">Completion Due (days)</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                min={1}
                                max={30}
                                value={field.value ?? ''}
                                onChange={(event) => {
                                  const value = event.target.value;
                                  field.onChange(value === '' ? undefined : Number(value));
                                }}
                                className="h-12 rounded-xl border-2 border-border focus:border-primary focus:ring-0"
                              />
                            </FormControl>
                            <p className="text-xs text-brand-secondary">How many days recipients have to complete the 1-on-1 form</p>
                            <FormMessage />
                          </FormItem>
                        )} />
                      </div>
                    </div>
                  )}

                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2"><Users className="h-5 w-5 text-primary" />Participants</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="flex items-center gap-4 p-4 bg-muted rounded-xl">
                          <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
                            <User className="h-6 w-6 text-primary" />
                          </div>
                          <div>
                            <div className="font-medium text-foreground">Manager</div>
                            <div className="text-sm text-muted-foreground">{getManagerName()}</div>
                          </div>
                        </div>

                        <div className="flex items-center gap-4 p-4 bg-muted rounded-xl">
                          <div className="w-12 h-12 bg-success/20 rounded-full flex items-center justify-center">
                            <User className="h-6 w-6 text-success" />
                          </div>
                          <div>
                            <div className="font-medium text-foreground">Employee</div>
                            <div className="text-sm text-muted-foreground">{getEmployeeName()}</div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="questions" className="space-y-6">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="text-xl font-bold text-brand mb-2">Questions</h3>
                      <p className="text-brand-secondary">Build your 1-on-1 question list using templates or custom questions</p>
                    </div>
                    <div className="text-sm text-brand-secondary">
                      {hasQuestions ? `${questionCount} question${questionCount === 1 ? '' : 's'} added` : 'No questions added yet'}
                    </div>
                  </div>

                  <div className="p-4 bg-muted rounded-xl">
                    <h4 className="font-semibold text-brand mb-3">Quick Templates</h4>
                    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3">
                      {QUICK_TEMPLATES.map((template) => (
                        <button
                          key={template.id}
                          type="button"
                          onClick={() => applyQuickTemplate(template.id)}
                          className="p-3 text-left bg-background rounded-lg border border-border hover:border-primary/30 transition-colors"
                        >
                          <div className="font-medium text-foreground text-sm">{template.name}</div>
                          <div className="text-xs text-muted-foreground mt-1">{template.questions.length} questions • {template.description}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {errors.questions && (
                    <div className="p-4 bg-destructive/5 border border-destructive/15 rounded-xl">
                      <div className="flex items-center gap-2">
                        <AlertCircle className="h-4 w-4 text-destructive" />
                        <p className="text-sm text-destructive font-medium">{(errors as any).questions.message}</p>
                      </div>
                    </div>
                  )}

                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <h4 className="font-semibold text-brand">Pre-defined Questions</h4>
                      <div className="flex items-center gap-3">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={toggleAllQuestions}
                          className="flex items-center gap-2 text-xs"
                          disabled={isAddingAllQuestions}
                        >
                          {areAllSampleQuestionsSelected ? <Minus className="h-3 w-3" /> : <Plus className="h-3 w-3" />}
                          {areAllSampleQuestionsSelected ? 'Remove All Questions' : 'Add All Questions'}
                        </Button>
                        <p className="text-sm text-muted-foreground">Click questions to add/remove from your list</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 gap-3 max-h-96 overflow-y-auto">
                      {SAMPLE_QUESTIONS.map((question) => {
                        const isSelected = selectedQuestionIds.has(question.id);
                        const isLoading = isTogglingQuestion === question.id;
                        return (
                          <div key={question.id} className={cn("flex items-start gap-3 p-4 rounded-xl border-2 transition-all cursor-pointer select-none", isSelected ? "border-primary bg-primary/10 shadow-sm" : "border-border bg-background hover:border-primary/30 hover:bg-primary/5", isLoading && "opacity-60 pointer-events-none")} onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (!isLoading) toggleQuestionSelection(question); }} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (!isLoading) toggleQuestionSelection(question); } }}>
                            <div className="flex items-center justify-center w-5 h-5 mt-0.5">
                              <Checkbox checked={isSelected} disabled className="data-[state=checked]:bg-primary data-[state=checked]:border-primary pointer-events-none" />
                            </div>
                            <div className="flex-1 space-y-2">
                              <p className="text-sm font-medium text-foreground">{question.text}</p>
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className={cn("text-xs", (question.type === 'text' || question.questionType === 'text') && "bg-muted text-primary border-border", (question.type === 'rating' || question.questionType === 'rating') && "bg-success/10 text-success border-success/20", (question.type === 'multiple_choice' || question.questionType === 'multiple_choice') && "bg-secondary/10 text-secondary-foreground border-secondary/20", (question.type === 'yes_no' || question.questionType === 'yes_no') && "bg-warning/10 text-warning border-warning/20", (question.type === 'likert' || question.questionType === 'likert') && "bg-accent/10 text-accent-foreground border-accent/20", !question.type && !question.questionType && "bg-muted text-muted-foreground border-muted")}>{(question.type || question.questionType || 'text').replace('_', ' ').toUpperCase()}</Badge>
                                {question.isRequired && (<Badge variant="outline" className="text-xs bg-destructive/5 text-destructive border-destructive/15">Required</Badge>)}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <h4 className="font-semibold text-brand">Questions ({questionCount})</h4>
                      <Button type="button" variant="outline" size="sm" onClick={addQuestion} className="flex items-center gap-2"><Plus className="h-4 w-4" />Add Question</Button>
                    </div>

                    <div className="space-y-3">
                      {safeWatchedQuestions.map((question: any, index: number) => (
                        <QuestionItem key={question._key} control={control} index={index} onRemove={removeQuestion} onMoveUp={moveQuestionUp} onMoveDown={moveQuestionDown} canMoveUp={index > 0} canMoveDown={index < safeWatchedQuestions.length - 1} watch={watch} setValue={setValue} />
                      ))}
                    </div>

                    {safeWatchedQuestions.length > 0 && (
                      <div className="pt-3 border-t border-border">
                        <Button type="button" variant="outline" size="sm" onClick={addQuestion} className="flex items-center gap-2 w-full justify-center"><Plus className="h-4 w-4" />Add Question</Button>
                      </div>
                    )}

                    {!hasQuestions && (
                      <div className="text-center p-8 border-2 border-dashed border-border rounded-xl bg-muted/50">
                        <MessageSquare className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
                        <p className="text-brand-secondary mb-2">No questions added yet</p>
                        <p className="text-sm text-muted-foreground">Choose from pre-defined questions above or add custom questions</p>
                        <Button type="button" variant="outline" size="sm" onClick={addQuestion} className="flex items-center gap-2 mt-3"><Plus className="h-4 w-4" />Add Your First Question</Button>
                      </div>
                    )}
                  </div>

                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2"><Save className="h-5 w-5 text-primary" />Save as Template</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <FormField control={control} name="saveAsTemplate" render={({ field }) => (
                        <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                          <FormControl>
                            <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                          </FormControl>
                          <div className="space-y-1 leading-none">
                            <FormLabel>Save these questions as a reusable template</FormLabel>
                            <p className="text-xs text-muted-foreground">Templates can be used for future 1-on-1 setups</p>
                          </div>
                        </FormItem>
                      )} />

                      {watchedSaveAsTemplate && (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                          <FormField control={control} name="templateName" render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-semibold text-brand">Template Name</FormLabel>
                              <FormControl>
                                <Input {...field} placeholder="e.g., Weekly Check-in Questions" className="h-12 rounded-xl border-2 border-border focus:border-primary focus:ring-0" />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )} />

                          <FormField control={control} name="templateDescription" render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-semibold text-brand">Description</FormLabel>
                              <FormControl>
                                <Input {...field} placeholder="Optional description" className="h-12 rounded-xl border-2 border-border focus:border-primary focus:ring-0" />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )} />
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="review" className="space-y-6">
                  <div>
                    <h3 className="text-xl font-bold text-brand mb-6">Review & Confirm</h3>

                    <div className="space-y-6">
                      <Card>
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2"><Settings className="h-5 w-5 text-primary" />Setup Summary</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          {watch("workflowType") === "email" ? (
                            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-6 gap-4">
                              <div className="bg-muted p-4 rounded-xl"><div className="text-xs font-medium text-brand-muted mb-1">Name</div><div className="font-semibold text-brand">{watchedTitle || '1-on-1 Session'}</div></div>
                              <div className="bg-muted p-4 rounded-xl"><div className="text-xs font-medium text-brand-muted mb-1">Type</div><div className="font-semibold text-brand capitalize flex items-center gap-2"><Mail className="h-4 w-4" />Email 1-on-1</div></div>
                              <div className="bg-muted p-4 rounded-xl"><div className="text-xs font-medium text-brand-muted mb-1">Frequency</div><div className="font-semibold text-brand">{getScheduleDescription(watchedSchedulePattern)}</div></div>
                              <div className="bg-muted p-4 rounded-xl"><div className="text-xs font-medium text-brand-muted mb-1">Start Date & Time</div><div className="font-semibold text-brand text-sm">{watch("startDate") && new Date(watch("startDate") as any).toLocaleDateString()}<br />{watch("startTime")}</div></div>
                              <div className="bg-muted p-4 rounded-xl"><div className="text-xs font-medium text-brand-muted mb-1">Questions</div><div className="font-semibold text-brand">{safeWatchedQuestions.length} questions</div></div>
                              <div className="bg-muted p-4 rounded-xl"><div className="text-xs font-medium text-brand-muted mb-1">Completion Due</div><div className="font-semibold text-brand">{watchedCompletionDueDays ?? 7} days</div></div>
                            </div>
                          ) : (
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                              <div className="bg-muted p-4 rounded-xl"><div className="text-xs font-medium text-brand-muted mb-1">Name</div><div className="font-semibold text-brand">{watchedTitle || '1-on-1 Session'}</div></div>
                              <div className="bg-muted p-4 rounded-xl"><div className="text-xs font-medium text-brand-muted mb-1">Type</div><div className="font-semibold text-brand capitalize flex items-center gap-2"><Video className="h-4 w-4" />Physical 1-on-1</div></div>
                              <div className="bg-muted p-4 rounded-xl"><div className="text-xs font-medium text-brand-muted mb-1">Questions</div><div className="font-semibold text-brand">{safeWatchedQuestions.length} questions</div></div>
                            </div>
                          )}
                        </CardContent>
                      </Card>

                      <Card>
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2"><Users className="h-5 w-5 text-primary" />Participants</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="bg-gradient-to-r from-primary/5 to-primary/10 p-4 rounded-lg">
                              <div className="flex items-center gap-2 mb-2">
                                <div className="w-8 h-8 bg-surface-variant rounded-full flex items-center justify-center"><User className="h-4 w-4 text-primary" /></div>
                                <div>
                                  <p className="text-xs text-primary font-medium">Manager</p>
                                  <p className="text-sm font-semibold text-gray-800">{getManagerName()}</p>
                                </div>
                              </div>
                            </div>

                            <div className="bg-gradient-to-r from-success/5 to-success/10 p-4 rounded-lg">
                              <div className="flex items-center gap-2 mb-2">
                                <div className="w-8 h-8 bg-success/20 rounded-full flex items-center justify-center"><User className="h-4 w-4 text-success" /></div>
                                <div>
                                  <p className="text-xs text-success font-medium">Employee</p>
                                  <p className="text-sm font-semibold text-gray-800">{getEmployeeName()}</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      <Card>
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2"><MessageSquare className="h-5 w-5 text-primary" />Questions Preview ({safeWatchedQuestions.length})</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-3">
                            {safeWatchedQuestions.map((question: any, index: number) => (
                              <div key={question.id} className="p-3 rounded-xl bg-muted">
                                <div className="font-medium text-brand">{index + 1}. {question.text}</div>
                                <div className="flex items-center gap-3 mt-2">
                                  <Badge variant="secondary" className="text-xs">
                                    {(question.type === "text" || question.questionType === "text") ? "Text Response" :
                                     (question.type === "rating" || question.questionType === "rating") ? "Rating (1-10)" :
                                     (question.type === "yes_no" || question.questionType === "yes_no") ? "Yes/No" :
                                     (question.type === "likert" || question.questionType === "likert") ? "Likert Scale" :
                                     "Multiple Choice"}
                                  </Badge>
                                  {question.isRequired && (<Badge variant="outline" className="text-xs text-destructive border-destructive/15">Required</Badge>)}
                                </div>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </div>

                </TabsContent>
              </Tabs>
              <div className="flex justify-between pt-6 border-t border-border bg-background">
                <Button type="button" variant="outline" onClick={handleCancel} className="gap-2 rounded-xl">Cancel</Button>
                <div className="flex gap-3">
                  {!isFirstTab && (
                    <Button type="button" variant="outline" onClick={goToPreviousTab} className="gap-2 rounded-xl">
                      <ArrowLeft className="h-4 w-4" />
                      Previous
                    </Button>
                  )}
                  {isLastTab ? (
                    <Button type="submit" className="gap-2 bg-gradient-to-r from-primary to-primary-hover hover:from-primary-hover hover:to-primary rounded-xl" disabled={isSubmitting}>
                      <UserCheck className="h-4 w-4" />
                      {isSubmitting ? 'Creating…' : 'Create 1-on-1 Setup'}
                    </Button>
                  ) : (
                    <Button type="button" onClick={goToNextTab} className="gap-2 bg-gradient-to-r from-primary to-primary-hover hover:from-primary-hover hover:to-primary rounded-xl">
                      Next
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </form>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
